/**
 * Shared type definitions for HALO NeighborhoodModule state management
 *
 * These types support commute state persistence between NeighborhoodSpoke (fullscreen)
 * and NeighborhoodModule (collapsed) views using React Context.
 */

import type { CommuteDestination, CommuteTime, TravelMode } from './utils/commuteTypes';

/**
 * Which view the neighborhood spoke is displaying
 */
export type NeighborhoodViewMode = 'map' | 'streetView';

/**
 * Status of commute calculation process
 */
export type CommuteStatus = 'idle' | 'calculating' | 'success' | 'error';

/**
 * Complete commute state data for persistence
 */
export type CommuteStateData = {
  /** Selected destination from autocomplete */
  destination: CommuteDestination | null;

  /** Calculated travel times for all modes */
  times: Array<CommuteTime>;

  /** Currently selected travel mode (highlighted card) */
  selectedMode: TravelMode | undefined;

  /** Current value in the autocomplete input field */
  inputValue: string;

  /** Status of commute calculation */
  status: CommuteStatus;

  /** Error message if calculation failed */
  error: string | null;

  /** Timestamp when commute was last calculated */
  lastCalculated: Date | null;

  /** Listing id whose origin produced the current `times`; used to detect staleness across listing changes */
  calculatedForListingId: string | undefined;
};

/**
 * Complete neighborhood state (extensible for future features)
 */
export type NeighborhoodState = {
  /** Commute calculation and persistence data */
  commute: CommuteStateData;

  /** Current view mode for the neighborhood spoke (map or street view) */
  viewMode: NeighborhoodViewMode;
};

/**
 * Action creators for updating neighborhood state
 */
export type NeighborhoodActions = {
  /** Set the selected commute destination and update input value */
  setCommuteDestination: (destination: CommuteDestination) => void;

  /** Set calculated commute times for all travel modes; records the listing id the calc was made against */
  setCommuteTimes: (times: Array<CommuteTime>, listingId?: string) => void;

  /**
   * Recalculate commute times for the active listing using the saved (or supplied) destination.
   * Idempotent for the same listing id while status is 'success'. Stale results from prior calls
   * are dropped via an internal sequence number, so concurrent calls are safe.
   */
  recalcForListing: (args: {
    origin: { lat: number; lng: number };
    listingId: string;
    destination?: CommuteDestination;
  }) => Promise<void>;

  /** Set the currently selected/highlighted travel mode */
  setSelectedMode: (mode: TravelMode | undefined) => void;

  /** Update the autocomplete input field value */
  setInputValue: (value: string) => void;

  /** Set commute calculation status (calculating, success, error) */
  setCommuteStatus: (status: CommuteStatus) => void;

  /** Set error message for failed commute calculations */
  setCommuteError: (error: string) => void;

  /** Clear all commute state and reset input for a fresh start */
  clearCommute: () => void;

  /** Switch between map and street view */
  setViewMode: (mode: NeighborhoodViewMode) => void;
};

/**
 * Context value type combining state and actions
 */
export type NeighborhoodContextValue = {
  state: NeighborhoodState;
  actions: NeighborhoodActions;
};

/**
 * Initial state for new NeighborhoodContext instances
 */
export const initialCommuteState: CommuteStateData = {
  destination: null,
  times: [],
  selectedMode: undefined,
  inputValue: '',
  status: 'idle',
  error: null,
  lastCalculated: null,
  calculatedForListingId: undefined,
};

export const initialNeighborhoodState: NeighborhoodState = {
  commute: initialCommuteState,
  viewMode: 'map',
};
